if (!customElements.get("product-form")) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      this.hasChildNodes() && this.init();
    }

    init() {
      this.form = this.querySelector(".js-product-form");
      if (!this.form) return;

      this.form.querySelector('[name="id"]').disabled = false;
      this.cartDrawer = document.querySelector("cart-drawer");
      this.submitButtons = this.getSubmitButtons();
      this.submitBtn = this.submitButtons[0];
      this.isSubmitting = false;

      if ("no-js" !== theme.settings.afterAtc) {
        this.form.addEventListener("submit", this.handleSubmit.bind(this));
      }
    }

    getSubmitButtons() {
      return Array.from(
        this.form.querySelectorAll('button[name="add"], input[type="submit"][name="add"]'),
      );
    }

    setSubmitButtonsBusy(isBusy) {
      this.submitButtons = this.getSubmitButtons();
      this.form.toggleAttribute("aria-busy", isBusy);

      this.submitButtons.forEach((button) => {
        if (isBusy) {
          button.setAttribute("aria-disabled","true");
          button.setAttribute("aria-busy","true");
          button.classList.add("is-loading");
        } else {
          button.removeAttribute("aria-disabled");
          button.removeAttribute("aria-busy");
          button.classList.remove("is-loading");
          button.classList.remove("is-success");
        }
      });
    }

    setSubmitButtonsSuccess() {
      this.submitButtons.forEach((button) => button.classList.add("is-success"));
    }

    async handleSubmit(event) {
      if ("product-signup_form" === event.target.id) return;

      event.preventDefault();
      this.submitButtons = this.getSubmitButtons();

      if (
        this.isSubmitting ||
        this.submitButtons.some((button) => button.getAttribute("aria-disabled") === "true")
      ) {
        return;
      }

      this.isSubmitting = true;
      theme.settings.vibrateOnATC &&
        window.navigator.vibrate &&
        window.navigator.vibrate(100);
      this.errorMsg = null;
      this.setErrorMsgState();
      this.setSubmitButtonsBusy(true);

      const formData = new FormData(this.form);
      let sections = "cart-icon-bubble";
      const cartDrawerSection = this.cartDrawer?.closest(".shopify-section");
      if (cartDrawerSection?.id) {
        sections += `,${cartDrawerSection.id.replace("shopify-section-", "")}`;
      }
      formData.append("sections_url", window.location.pathname);
      formData.append("sections", sections);

      const request = {
        method: "POST",
        headers: {
          Accept: "application/javascript",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: formData,
      };
      let requestSucceeded = false;

      try {
        const addResponse = await fetch(theme.routes.cartAdd, request);
        const addedItem = await addResponse.json();
        let errorMessage =
          typeof addedItem.description === "string"
            ? addedItem.description
            : addedItem.message;

        if (addedItem.errors && typeof addedItem.errors === "object") {
          errorMessage = Object.entries(addedItem.errors).map((entry) =>
            entry[1].join(", "),
          );
        }
        if (addedItem.status) this.setErrorMsgState(errorMessage);
        if (!addResponse.ok) throw new Error(addResponse.status);

        if ("page" === theme.settings.afterAtc) {
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          ProductForm.updateCartIcon(addedItem);
          const quickAddDrawer = this.closest("quick-add-drawer");
          if (quickAddDrawer) quickAddDrawer.addedToCart();
          setTimeout(() => {
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(
                addedItem,
                !quickAddDrawer && "drawer" === theme.settings.afterAtc,
              );
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector("cart-items");
              if (cartItems) {
                "true" === cartItems.dataset.empty
                  ? window.location.reload()
                  : cartItems.refresh();
              }
            }
          }, 700);
        }

        requestSucceeded = true;
        this.dispatchEvent(
          new CustomEvent("on:cart:success", {
            bubbles: true,
            detail: { variantId: addedItem.variant_id },
          }),
        );

        // The item is already in Shopify's cart once /cart/add succeeds. Read
        // the refreshed cart only for secondary UI events; it must never delay
        // or reverse a completed add-to-cart request.
        void this.dispatchCartMutationEvent(addedItem);
      } catch (error) {
        console.log(error);
        const currentError = this.errorMsg ? this.errorMsg.textContent.trim() : "";
        const connectionError = error instanceof TypeError
          || /failed to fetch|networkerror|load failed/i.test(error.message || "");
        const fallbackError = /^\d+$/.test(error.message)
          ? theme.strings.cartError
          : connectionError
            ? "We couldn't add this item. Check your connection and try again."
            : error.message;
        const errorMessage = currentError
          || fallbackError
          || "This item could not be added. Check your connection and try again.";
        this.setErrorMsgState(errorMessage);
        this.dispatchEvent(
          new CustomEvent("on:cart:error", {
            bubbles: true,
            detail: {
              error: errorMessage,
            },
          }),
        );
      } finally {
        if (requestSucceeded) {
          this.setSubmitButtonsSuccess();
          setTimeout(() => {
            this.setSubmitButtonsBusy(false);
            this.isSubmitting = false;
          }, 1400);
        } else {
          this.setSubmitButtonsBusy(false);
          this.isSubmitting = false;
        }
      }
    }

    async dispatchCartMutationEvent(addedItem) {
      try {
        const currentCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!currentCartResponse.ok) throw new Error(currentCartResponse.status);
        const currentCart = await currentCartResponse.json();
        const currentLineItem = Array.isArray(currentCart?.items)
          ? currentCart.items.find((item) => item.variant_id === addedItem.variant_id)
          : null;
        const addedQuantity = Number(addedItem.quantity) || 1;
        const currentQuantity = Number(currentLineItem?.quantity) || addedQuantity;
        const previousQuantity = Math.max(0, currentQuantity - addedQuantity);

        if (previousQuantity > 0) {
          this.dispatchEvent(
            new CustomEvent("on:line-item:change", {
              bubbles: true,
              detail: {
                cart: currentCart,
                variantId: addedItem.variant_id,
                oldQuantity: previousQuantity,
                newQuantity: currentQuantity,
              },
            }),
          );
        } else {
          this.dispatchEvent(
            new CustomEvent("on:cart:add", {
              bubbles: true,
              detail: { cart: currentCart, variantId: addedItem.variant_id },
            }),
          );
        }
      } catch (error) {
        console.warn("Cart item was added, but the refreshed cart could not be read.", error);
      }
    }

    static updateCartIcon(data) {
      const cartIcon = document.getElementById("cart-icon-bubble");
      if (cartIcon) cartIcon.innerHTML = data.sections["cart-icon-bubble"];
    }

    setErrorMsgState(message = false) {
      this.errorMsg = this.errorMsg || this.querySelector(".js-form-error");
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !message;
      if (!message) return;

      this.errorMsg.innerHTML = "";
      (Array.isArray(message) ? message : [message]).forEach((item, index) => {
        if (index > 0) this.errorMsg.insertAdjacentHTML("beforeend", "<br>");
        this.errorMsg.insertAdjacentText("beforeend", item);
      });
    }
  }

  customElements.define("product-form", ProductForm);
}
